/**
 * Compose a send snapshot from a plain source object ({ input, attachedFiles,
 * referencedExpenses, voiceSegments, replyingToMessages }).
 *
 * Pure + module-level so EVERY composer produces the EXACT same payload shape:
 *  • the Main Office on-screen composer (`buildSendSnapshot`, from live state),
 *  • the Main Office origin-bound send flush (`buildSnapshotFromDraft`, from a
 *    persisted draft the user navigated away from),
 *  • the Telegram Bucket Day Focus workspace (#173 Full Tool Parity) —
 *    which reuses the same <ChatInput> and therefore must build the same
 *    `[Typed]` / `[Voice N]` labelled content, the same `attached_documents`,
 *    and the same hybrid `text_content` / `audio_transcriptions` split.
 *
 * Extracted from MainOfficeView (was a private module-level function there) when
 * #173 gave the bucket views full composer parity — one builder, one contract.
 *
 * Returns null when there is nothing to send.
 */

// ── Pre-send transcription corrections (transcription edit #252) ─────────────
// A composer segment may carry `editedTranscription`: the user's manual
// correction of the raw STT, typed into the composer's "Show transcription"
// panel BEFORE sending. The raw `transcription` is never overwritten, so the
// sent message can keep the #130/#228 contract:
//   • `transcription`         → the faithful audio record ("Show transcription")
//   • `edited_transcription`  → the user's correction (the elevated
//                               "Voice Message N Edited:" block)
//   • `edited: true`          → renders that block + marks the correction
// and the message CONTENT (what the AI runs on) uses the correction.
//
// A correction only counts when it is non-empty AND genuinely differs from the
// original (trim-insensitive): an untouched — or reverted, or blanked — clip
// produces a byte-identical standard voice note with no extra keys (Scenario A).
// The Telegram bucket composer edits `transcription` in place and never
// sets this field, so its payload is unchanged.
const segmentEditText = (s) => (typeof s?.editedTranscription === 'string' ? s.editedTranscription : null);
const segmentIsEdited = (s) => {
    const edited = segmentEditText(s);
    if (edited == null) return false;
    const trimmed = edited.trim();
    return trimmed !== '' && trimmed !== (s.transcription || '').trim();
};

export function composeSnapshot({
    input = '',
    attachedFiles = [],
    referencedExpenses = [],
    voiceSegments = [],
    replyingToMessages = [],
} = {}) {
    const typed = (input || '').trim();
    const hasText = !!typed;
    const hasAttachments = attachedFiles.length > 0;
    const hasRefs = referencedExpenses.length > 0;
    const hasSegments = voiceSegments.length > 0;
    if (!hasText && !hasAttachments && !hasRefs && !hasSegments) return null;

    // #513: an unpacked ZIP has no document id — it travels as its
    // `archive_import` block (+ the display `summary`); the server renders it
    // in the attachment manifest so the reply states what was unpacked.
    const attachedDocuments = attachedFiles
        .filter(f => (f.id || f.archive_import) && !f.upload_error)
        .map(f => (f.id ? {
            id: f.id,
            filename: f.filename,
            mime_type: f.mime_type,
            file_size: f.file_size,
            is_image: !!f.is_image,
        } : {
            filename: f.filename,
            mime_type: f.mime_type || 'application/zip',
            file_size: f.file_size,
            is_image: false,
            archive_import: f.archive_import,
            ...(f.summary ? { summary: f.summary } : {}),
        }));

    const dispatchSegments = voiceSegments
        .filter(s => s.status === 'ready' && s.audioUrl)
        .map(s => {
            const base = {
                audio_url: s.audioUrl,
                gcs_path: s.gcsPath,
                duration: s.duration,
                mime_type: s.mimeType,
                transcription: s.transcription || '', // ORIGINAL audio record — never overwritten
            };
            return segmentIsEdited(s)
                ? { ...base, edited_transcription: segmentEditText(s), edited: true }
                : base;
        });
    const segmentBlobUrls = voiceSegments.map(s => s.blobUrl).filter(Boolean);

    // Display + backend content carries explicit labels so the LLM can tell typed
    // text apart from each voice transcription. A corrected clip contributes its
    // CORRECTION — the pre-send edit is the source of truth for the turn (#252).
    const audioTranscriptions = dispatchSegments
        .map(s => (s.edited ? s.edited_transcription : s.transcription))
        .filter(Boolean);
    const labeledParts = [];
    if (typed) labeledParts.push(`[Typed]: ${typed}`);
    audioTranscriptions.forEach((t, idx) => {
        labeledParts.push(`[Voice ${idx + 1}]: ${t}`);
    });
    // If there's only one source (typed-only OR a single voice clip), skip the
    // labels — they add noise for the common case.
    const displayContent = (typed && audioTranscriptions.length === 0)
        ? typed
        : (!typed && audioTranscriptions.length === 1)
            ? audioTranscriptions[0]
            : labeledParts.join('\n\n');

    const backendContent = displayContent || (attachedDocuments.length > 0
        ? (attachedDocuments.length === 1
            ? `[Attached ${attachedDocuments[0].filename}]`
            : `[Attached ${attachedDocuments.length} files]`)
        : (referencedExpenses.length > 0
            ? `[Referencing ${referencedExpenses.length} expense${referencedExpenses.length === 1 ? '' : 's'}]`
            : ''));

    return {
        typed,
        attachedDocuments,
        dispatchSegments,
        segmentBlobUrls,
        audioTranscriptions,
        displayContent,
        backendContent,
        replyingTo: replyingToMessages.length > 0 ? replyingToMessages : null,
        referencedExpensesSnapshot: referencedExpenses.length > 0 ? [...referencedExpenses] : null,
    };
}

export default composeSnapshot;
